import * as React from 'react';
import ButtonMUI, { ButtonProps as ButtonPropsMUI } from '@material-ui/core/Button';
import { makeStyles } from '@material-ui/core/styles';

import { defaultTheme, WithMaterialThemeProps, pxToRem } from '../../styles';
import Spinner from '../spinner';
import { Root } from './styled';


export type ButtonProps = {
	appearance?: 'contained' | 'outlined' | 'text';
	color?: 'primary' | 'default' | 'white';
	size?: 'small' | 'medium' | 'large';
	isFetching?: boolean;
	stopPropagation?: boolean;
	onClick?: (e: React.SyntheticEvent<HTMLButtonElement>) => void;
} & Omit<ButtonPropsMUI,
	'appearance'
	| 'color'
	| 'size'
	| 'onClick'>

const useStyles = makeStyles((materialTheme: WithMaterialThemeProps) => {
	const { customTheme } = materialTheme;
	const isThemeActivated = Boolean(customTheme);
	const theme = customTheme || defaultTheme; 
	const {
		fontSize,
		fontWeight,
		lineHeight
	} = theme.typography.button;
	const getBgColor = (props: ButtonProps) => {
		if (props.appearance !== 'contained') {
			return undefined;
		}

		const map = {
			'primary': theme.color.accentPrimary,
			'default': theme.color.whitePrimary,
			'white': theme.color.whitePrimary,
		};

		return map[props.color as string];
	};
	const getBgHoverColor = (props: ButtonProps) => {

		if (props.appearance === 'outlined' && props.color === 'white') {
			return 'rgba(255, 255, 255, 0.2)';
		}

		if (props.appearance !== 'contained') {
			return undefined;
		}

		const map = {
			'primary': theme.color.accentPrimaryHover,
			'default': theme.color.whitePrimary,
		};

		return map[props.color as string];
	};
	const getColor = (props: ButtonProps) => {
		if (props.appearance === 'outlined') {
			if (props.color === 'white') {
				return theme.color.whitePrimary;
			}

			return undefined;
		}

		const map = {
			'primary': props.appearance === 'text' ? theme.color.accentPrimary : theme.color.whitePrimary,
			'default': props.size === 'small' ? theme.color.blackSecondary : theme.color.blackPrimary,
			'white': theme.color.whitePrimary,
		};

		return map[props.color as string];
	};
	const getPaddings = (props: ButtonProps) => {
		const map = {
			'small': '4px 2px',
			'medium': '6px 16px',
			'large': '6px 16px',
		};

		return map[props.size as string];
	};
	const getMinWidth = (props: ButtonProps) => {
		const map = {
			'small': 0,
			'medium': 150,
			'large': 150,
		};

		return map[props.size as string];
	};
	const getMinHeight = (props: ButtonProps) => {
		const map = {
			'small': 0,
			'medium': 36,
			'large': 36,
		};

		return map[props.size as string];
	};
	const getBoxShadow = (props: ButtonProps) => {
		const map = {
			'contained': '0 1px 1px 0 rgba(0, 0, 0, 0.1), 0 0 1px 0 rgba(0, 0, 0, 0.05)',
			'outlined': '0 1px 1px 0 rgba(0, 0, 0, 0.1), 0 0 1px 0 rgba(0, 0, 0, 0.05)',
			'text': undefined,
		};

		return map[props.appearance as string];
	};
	const getFontSize = (props: ButtonProps) => {
		const map = {
			'small': pxToRem(12),
			'medium': fontSize,
			'large': fontSize,
		};

		return map[props.size as string];
	};
	const geTextTransform = (props: ButtonProps) => {
		const map = {
			'text:small': 'none',
		};

		return map[`${props.appearance}:${props.size}`] || 'uppearcase';
	};

	const getBorderColor = (props: ButtonProps) => {
		const {
			appearance,
			size,
			color,
			disabled,
		} = props;
		const map = {
			'outlined:small:white': theme.color.whitePrimary,
			'outlined:medium:primary': disabled ? theme.color.blackDisabled : theme.color.accentPrimary,
		};
		const key = `${appearance}:${size}:${color}`;

		return map[key] || theme.color.accentPrimary;
	};

	const getFallbackStyles = () => {
		if (isThemeActivated) return {};

		return {
			'&.MuiButton-outlined': {
				border: `1px solid ${theme.color.blackSecondary}`
			},
			'&.MuiButton-outlinedPrimary': {
				color: theme.color.accentPrimary,
				border: `1px solid ${theme.color.accentPrimaryPressed}`
			},
			'&.MuiButton-outlinedPrimary:hover': {
				backgroundColor: 'rgba(38, 208, 109, 0.1)'
			},
			'&.MuiButton-textPrimary': {
				color: theme.color.accentPrimary,
			},
			'&.MuiButton-textPrimary:hover': {
				backgroundColor: 'rgba(38, 208, 109, 0.1)'
			}
		}
	};

	return {
		button: (props: ButtonProps) => ({
			...getFallbackStyles(),
			padding: getPaddings(props),
			minWidth: getMinWidth(props),
			minHeight: getMinHeight(props),
			borderRadius: 1,
			textTransform: geTextTransform(props),
			color: getColor(props),
			fontSize: getFontSize(props),
			fontWeight,
			lineHeight,
			backgroundColor: getBgColor(props),
			boxShadow: getBoxShadow(props),
			'&:hover': {
				backgroundColor: getBgHoverColor(props)
			},
			'&.Mui-disabled': {
				color: theme.color.whitePrimary,
				backgroundColor: theme.color.blackDisabled
			},
			'&.MuiButton-outlined': {
				border: `1px solid ${getBorderColor(props)}`
			}
		} as any)
	}
});

const Button: React.SFC<ButtonProps> = (props) => {
	const {
		appearance,
		color,
		size,
		isFetching,
		stopPropagation,
		className = '',
		fullWidth,
		onClick,
		children,
		...rest
	} = props;
	const classes = useStyles(props);
	const handleClick = React.useCallback((e: React.SyntheticEvent<HTMLButtonElement>) => {
		if (stopPropagation) {
			e.stopPropagation();
		}

		!isFetching && typeof onClick === 'function' && onClick(e);
	}, [onClick]);

	const renderSpinner = () => {
		const getColor = (appearance, color) => {
			const map = {
				'contained:primary': 'white',
				'contained:default': 'primary',
				'outlined:primary': 'primary',
				'outlined:default': 'primary',
				'text:primary': 'primary',
				'text:default': 'primary',
			};

			return map[`${appearance}:${color}`]
		};

		return (
			<Spinner
				appearance='embedded'
				color={getColor(appearance, color)}
			/>
		);
	};

	return (
		<Root
			size={size}
			fullWidth={fullWidth}>
			<ButtonMUI
				{...rest}
				variant={appearance as any}
				color={color}
				size={size}
				className={`${classes.button} ${className}`}
				fullWidth={fullWidth}
				onClick={handleClick}>
				{
					isFetching
					? renderSpinner()
					: children
				}
			</ButtonMUI>
		</Root>
	);
}

Button.displayName = 'Button';
Button.defaultProps = {
	appearance: 'contained',
	color: 'primary',
	size: 'medium'
};

export default Button;
